import {
  FloatingIp,
  FloatingIpChange,
  type CreateFloatingIpRequest,
  type ReverseDnsRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

const key = (projectId: string) => ["projects", projectId, "floating-ips"] as const;

/** The project's floating IPs; live updates refresh them through the `projects` key. */
export const useFloatingIps = (projectId: string) =>
  useQuery({
    queryKey: key(projectId),
    queryFn: () => api(`/projects/${projectId}/floating-ips`, { schema: z.array(FloatingIp) }),
  });

/** Writes also change what server pages show, so every project query is refreshed. */
function useFloatingMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ["projects"] }),
  });
}

const base = (projectId: string) => `/projects/${projectId}/floating-ips`;

export const useCreateFloatingIp = (projectId: string) =>
  useFloatingMutation((body: CreateFloatingIpRequest) =>
    api(base(projectId), {
      method: "POST",
      body,
      schema: FloatingIpChange,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useUpdateFloatingIp = (projectId: string) =>
  useFloatingMutation(
    ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      description?: string;
      labels?: Record<string, string>;
    }) => api(`${base(projectId)}/${id}`, { method: "PATCH", body, schema: FloatingIp }),
  );

const useFloatingAction = <B>(projectId: string, name: string) =>
  useFloatingMutation(({ id, body }: { id: string; body?: B }) =>
    api(`${base(projectId)}/${id}/actions/${name}`, {
      method: "POST",
      ...(body === undefined ? {} : { body }),
      schema: FloatingIpChange,
    }),
  );

export const useAssignFloatingIp = (projectId: string) =>
  useFloatingAction<{ serverId: string }>(projectId, "assign");
export const useUnassignFloatingIp = (projectId: string) =>
  useFloatingAction<never>(projectId, "unassign");
export const useFloatingReverseDns = (projectId: string) =>
  useFloatingAction<ReverseDnsRequest>(projectId, "reverse-dns");
export const useFloatingIpProtection = (projectId: string) =>
  useFloatingAction<{ delete: boolean }>(projectId, "protection");

export const useDeleteFloatingIp = (projectId: string) =>
  useFloatingMutation((id: string) => api(`${base(projectId)}/${id}`, { method: "DELETE" }));
